import React, { useState, useEffect, useCallback } from 'react';
import { scheduleApi } from '../../services/api';
import { formatInTimeZone } from 'date-fns-tz';
import Avatar from '../common/Avatar';

const HALF_HOUR = 30 * 60 * 1000;
const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Collapse the schedule's 30-min slots into the block covering `now` (status 'active'),
// or the next upcoming block (status 'next'). Consecutive slots by the same operator
// with no gap merge into one contiguous range (so 2–4pm shows as one 2h block).
function computeBlock(slots, nowMs) {
  const sorted = [...slots].sort((a, b) => new Date(a.slotStart) - new Date(b.slotStart));
  let idx = sorted.findIndex(s => {
    const st = new Date(s.slotStart).getTime();
    return nowMs >= st && nowMs < st + HALF_HOUR;
  });
  let status = 'active';
  if (idx === -1) {
    idx = sorted.findIndex(s => new Date(s.slotStart).getTime() > nowMs);
    status = 'next';
  }
  if (idx === -1) return null;

  const opId = sorted[idx].operatorId;
  const contiguous = (a, b) => a.operatorId === opId
    && new Date(a.slotStart).getTime() + HALF_HOUR === new Date(b.slotStart).getTime();
  let startI = idx, endI = idx;
  while (startI > 0 && contiguous(sorted[startI - 1], sorted[startI])) startI--;
  while (endI < sorted.length - 1 && contiguous(sorted[endI], sorted[endI + 1])) endI++;

  return {
    status,
    operatorName: sorted[idx].operatorName,
    operatorAvatar: sorted[idx].operatorAvatar,
    operatorId: opId,
    startIso: sorted[startI].slotStart,
    endIso: new Date(new Date(sorted[endI].slotStart).getTime() + HALF_HOUR).toISOString(),
  };
}

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// Shows who is scheduled to control this camera, in the camera's timezone. Displays the
// operator on now ("Scheduled"), otherwise the next one within a week ("Next"), otherwise
// "No operator scheduled". Refreshes each minute so it rolls over with the schedule.
export default function ScheduledOperator({ camera }) {
  const [block, setBlock] = useState(undefined); // undefined = loading, null = none scheduled
  const tz = camera.timezone || browserTz;

  const load = useCallback(async () => {
    try {
      const now = Date.now();
      const from = new Date(now - HALF_HOUR).toISOString();
      const to = new Date(now + 7 * 24 * HALF_HOUR * 2).toISOString(); // ~7 days ahead
      const res = await scheduleApi.list(camera.id, from, to);
      setBlock(computeBlock(res.data.slots || [], Date.now()));
    } catch {
      setBlock(null);
    }
  }, [camera.id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (block === undefined) return null; // avoid a flash before the first load

  const baseStyle = {
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    fontFamily: 'var(--font-display)', fontSize: '13px',
  };

  if (!block) {
    return (
      <div style={{ ...baseStyle, color: 'var(--text-muted)' }}>
        <CalendarIcon />
        <span style={{ fontWeight: 600 }}>No operator scheduled</span>
      </div>
    );
  }

  const todayLocal = formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');
  const blockLocal = formatInTimeZone(block.startIso, tz, 'yyyy-MM-dd');
  const dayPrefix = blockLocal !== todayLocal ? formatInTimeZone(block.startIso, tz, 'EEE MMM d, ') : '';
  const startStr = formatInTimeZone(block.startIso, tz, 'h:mm a');
  const endStr = formatInTimeZone(block.endIso, tz, 'h:mm a');
  const zone = formatInTimeZone(block.startIso, tz, 'zzz');
  const isActive = block.status === 'active';
  const accent = isActive ? 'var(--accent-green)' : 'var(--accent-cyan)';

  return (
    <div style={baseStyle}>
      <span style={{ color: accent, display: 'flex', alignItems: 'center' }}><CalendarIcon /></span>
      <span style={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '11px', color: accent }}>
        {isActive ? 'Scheduled' : 'Next'}
      </span>
      <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
        {dayPrefix}{startStr} – {endStr} {zone}
      </span>
      <span style={{ color: 'var(--text-muted)' }}>·</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Avatar src={block.operatorAvatar} name={block.operatorName} userId={block.operatorId} size={22} />
        <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{block.operatorName}</span>
      </span>
    </div>
  );
}
